import { ReactNode, useRef, useState, useEffect } from "react";

interface StickyHeaderProps {
  /** Content for the left slot (e.g., back button, settings button, or left-aligned title) */
  leftContent?: ReactNode;
  /** Title text displayed in center (optional if using custom centerContent) */
  title?: string;
  /** Custom center content (overrides title if provided) */
  centerContent?: ReactNode;
  /** Content for the right slot (e.g., share button, notifications) */
  rightContent?: ReactNode;
  /** Use semi-transparent background with backdrop blur */
  transparent?: boolean;
  /** Additional classes for the header container */
  className?: string;
  /** Additional content below the header row (e.g., search bar, filters) */
  children?: ReactNode;
}

export function StickyHeader({
  leftContent,
  title,
  centerContent,
  rightContent,
  transparent = false,
  className = "",
  children,
}: StickyHeaderProps) {
  const headerRef = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    if (!headerRef.current) return;

    const updateHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(headerRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  return (
    <>
      {/* Fixed Header */}
      <header
        ref={headerRef}
        className={`
          fixed top-0 left-0 right-0 z-30
          pt-[calc(env(safe-area-inset-top)+1rem)]
          ${
            transparent
              ? "backdrop-blur-md bg-background-light/80 dark:bg-background-dark/80"
              : "bg-background-light dark:bg-background-dark"
          }
          ${className}
        `.trim().replace(/\s+/g, ' ')}
      >
        {/* Header Row */}
        <div className="flex items-center justify-between px-4 pb-2">
          {/* Left slot - fixed width for alignment */}
          <div className="flex w-12 shrink-0 items-center justify-start">
            {leftContent}
          </div>

          {/* Center slot - flexible, takes remaining space */}
          <div className="flex-1 text-center min-w-0">
            {centerContent || (
              title && (
                <h2 className="text-lg font-bold leading-tight tracking-[-0.015em] text-gray-900 dark:text-white truncate">
                  {title}
                </h2>
              )
            )}
          </div>

          {/* Right slot - fixed width for alignment */}
          <div className="flex w-12 shrink-0 items-center justify-end">
            {rightContent}
          </div>
        </div>

        {/* Additional content (search bar, filters, etc.) */}
        {children}
      </header>

      {/* Spacer to prevent content from hiding behind fixed header */}
      <div style={{ height: headerHeight }} />
    </>
  );
}
